<!-- Pagination bar (PrimeNG paginator). It is only a VIEW of the state owned by ListComponent (page, page size, total);
     the table below receives just `pageItems`, so only the current page is ever rendered. -->
<ng-template #pager>
  <nav aria-label="Notes pagination" class="px-1 max-md:[--p-paginator-nav-button-width:2.75rem] max-md:[--p-paginator-nav-button-height:2.75rem]">
    <p-paginator
      [styleClass]="isLargeScreen
        ? '!flex-wrap !justify-between !gap-x-3 !gap-y-1 !border-0 !bg-transparent !px-0 !py-1 text-sm'
        : '!flex-nowrap !justify-between !gap-x-1.5 !border-0 !bg-transparent !px-0 !py-1 text-xs'"
      [rows]="paginatorRows"
      [first]="paginatorFirst"
      [totalRecords]="totalRecords"
      [showPageLinks]="isLargeScreen"
      [showFirstLastIcon]="isLargeScreen"
      [pageLinkSize]="5"
      [alwaysShow]="true"
      [templateLeft]="pagerLeft"
      [templateRight]="pagerRight"
      (onPageChange)="onPageChange($event)"
    ></p-paginator>
  </nav>
</ng-template>

<ng-template #pagerLeft>
  <!-- On mobile the whole paginator is forced onto a single row (see [styleClass] above), so the
       separate "Page X of Y" line that used to sit under the count on mobile is dropped — the count
       plus the Prev/Next buttons already convey position, and desktop never showed this line anyway
       (it shows numbered page links instead). -->
  <div class="flex flex-row items-baseline gap-3 leading-tight text-[var(--textColorFaded)] whitespace-nowrap" aria-live="polite">
    <span class="font-medium text-[var(--textColor)]">{{ resultCountLabel }}</span>
  </div>
</ng-template>

<ng-template #pagerRight>
  <label class="flex items-center gap-2 text-xs text-[var(--textColorFaded)]">
    <span class="hidden sm:inline">Notes per page</span>
    <p-select
      [options]="pageSizeOptions"
      optionLabel="label"
      optionValue="value"
      [ngModel]="pageSize"
      (ngModelChange)="onPageSizeChange($event)"
      ariaLabel="Notes per page"
      appendTo="body"
      styleClass="!min-h-11 md:!min-h-9"
    ></p-select>
  </label>
</ng-template>

<!-- Only ONE layout is rendered at a time (chosen by the same breakpoint the old lg:hidden / lg:block classes
     used). Rendering both meant every note existed twice in the DOM, one copy always hidden. -->
@if (!isLargeScreen) {
<div class="p-1">
  <!-- Mobile card list: a plain divided list of 2-line rows (Title, then a smaller Modified+Tags
       line) instead of a data-grid table — closer to how a modern mobile note app presents a list.
       Pagination is shown only at the top now, directly under the header pane, instead of the
       bottom (per request) — the first thing visible below the header on page load.
       Column sorting (previously via clickable table headers) has no equivalent in this layout; the
       list simply keeps whatever sort was last chosen (defaults to Modified, newest first) — say if
       a lightweight "Sort by…" control is wanted back for mobile. -->
  @if (pageItems.length > 10) {
    <ng-container *ngTemplateOutlet="pager"></ng-container>
  }
  <div
    role="list"
    class="divide-y divide-gray-300 dark:divide-slate-600 border-t border-b border-gray-300 dark:border-slate-600 rounded-md overflow-hidden">
    @for (note of pageItems; track note._id) {
      <!-- Neutral row background (white / near-black in dark mode) — no per-project tint here, unlike
           the desktop table; differentiation comes from the text/tags, keeping the list plain and clean. -->
      <!-- Long press (touch/pen only, ~550 ms, cancelled by >10 px movement or scrolling) opens the note
           action menu next to it; a short tap still opens the note. select-none + no iOS callout only on the
           row, so the platform's text-selection/link menu doesn't compete with the long press. -->
      <div
        role="listitem"
        (click)="clickRow(note)"
        (pointerdown)="onRowPointerDown($event, note)"
        (pointermove)="onRowPointerMove($event)"
        (pointerup)="onRowPointerEnd($event)"
        (pointercancel)="onRowPointerEnd($event)"
        (contextmenu)="onRowContextMenu($event)"
        class="flex min-h-11 cursor-pointer select-none items-center gap-2 bg-surface-0 px-3 py-2 text-black transition-colors [-webkit-touch-callout:none] hover:bg-gray-100 dark:bg-surface-900 dark:text-slate-100 dark:hover:!bg-slate-800"
        [ngClass]="{
          'ring-2 ring-inset ring-primary-600': isNoteSelected(note._id),
          '!bg-gray-100 dark:!bg-slate-800': pressedNoteId === note._id || noteMenuNote?._id === note._id
        }">
        <div class="min-w-0 flex-1">
          <!-- Line 1: Title. Not yet synced: the same animated red clock as the desktop table, instead of a red title. -->
          <!-- -ml-1/pl-1 and -my-0.5/py-0.5: room inside `truncate`'s overflow clip for the clock's pulsing halo, layout unchanged. -->
          <div
            class="-my-0.5 -ml-1 truncate py-0.5 pl-1"
            [ngClass]="{
              'font-bold': isEmphasizedStatus(note.Status),
              'italic !text-gray-500 dark:!text-gray-400': isMutedStatus(note.Status)
            }"
            [title]="isNotSynced(note) ? syncPendingLabel(note) : ''"
          >@if (isNotSynced(note)) {<span class="sync-pending-clock" role="img" [attr.aria-label]="syncPendingLabel(note)"><svg viewBox="0 0 24 24" aria-hidden="true"><circle class="sync-pending-clock__face" cx="12" cy="12" r="9.25" /><line class="sync-pending-clock__hand sync-pending-clock__hand--hour" x1="12" y1="12" x2="12" y2="7.5" /><line class="sync-pending-clock__hand sync-pending-clock__hand--minute" x1="12" y1="12" x2="12" y2="5" /><circle cx="12" cy="12" r="1.3" class="sync-pending-clock__pin" /></svg></span>&nbsp;}<span data-testid="note-title" [class.line-through]="isStruckStatus(note.Status) || note.syncStatus === 'deleted'">{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</span><span *ngIf="isFavoriteNote(note)" title="Favorite"> ❤️</span><span *ngIf="note.Attachments?.length" title="Has attachments"> 📎</span></div>
          <!-- Line 2 (smaller font): Modified date, then Tags -->
          <div class="mt-0.5 flex items-center gap-1.5 overflow-x-auto whitespace-nowrap text-xs text-[var(--textColorFaded)]">
            <span class="flex-shrink-0">{{ formatDate(note.last_modified) }}</span>
            <span class="flex-shrink-0" aria-hidden="true">·</span>
            @if (isInSharedSpace(note)) {
              <span class="flex-shrink-0 inline-flex items-center gap-0.5 text-red-600 dark:text-red-400" title="Shared Space">
                <span class="material-symbols-outlined !text-sm leading-none" aria-hidden="true">group</span>{{ getSpaceName(note) }}
              </span>
            } @else {
              <span class="flex-shrink-0">{{ getSpaceName(note) }}</span>
            }
            @if (getTagDisplayItems(note).length) {
              <span class="flex-shrink-0" aria-hidden="true">·</span>
              @for (tag of getTagDisplayItems(note); track tag.name) {
                <span class="note-tag-chip inline-flex flex-shrink-0 items-center gap-1 whitespace-nowrap rounded-full border border-gray-300 bg-white px-1.5 py-0.5 dark:border-slate-600 dark:bg-slate-800">
                  <span
                    class="h-1.5 w-1.5 flex-shrink-0 rounded-full border border-black/10"
                    [style.backgroundColor]="tag.color || '#94a3b8'"
                    [title]="tag.color || 'No tag color'"></span>
                  @if (tag.isPrivate) {
                    <i class="pi pi-lock !text-[0.55rem] opacity-70" aria-label="Private tag"></i>
                  }
                  <span>{{ tag.name }}</span>
                </span>
              }
            }
          </div>
        </div>
        <!-- Priority (not shown for Normal) and Action required: compact icons, labelled for screen readers. -->
        @if (isActionRequired(note) || priorityIndicator(note.priority).icon) {
          <div class="flex flex-shrink-0 items-center gap-1.5 self-start pt-0.5">
            @if (priorityIndicator(note.priority); as p) {
              @if (p.icon) {
                <span class="inline-flex" [ngClass]="p.colorClass" role="img" [attr.aria-label]="p.label" [title]="p.label" data-testid="priority-indicator"><i class="text-sm" [ngClass]="p.icon" aria-hidden="true"></i></span>
              }
            }
            @if (isActionRequired(note)) {
              <span class="inline-flex text-orange-600 dark:text-orange-400" role="img" aria-label="Action required" title="Action required" data-testid="action-required-indicator"><i class="pi pi-flag-fill text-sm" aria-hidden="true"></i></span>
            }
          </div>
        }
      </div>
    } @empty {
      <div class="px-3 py-6 text-center text-sm text-[var(--textColorFaded)]">No notes to show.</div>
    }
  </div>
</div>

<!-- Long-press note action menu (mobile only), shown right next to the pressed note: below it, or
     above it when there is no room below. The same actions remain available from the note's View page.
     The light backdrop takes the dismissing tap, so closing the menu never also opens another note. -->
@if (noteMenuVisible) {
  <div class="fixed inset-0 z-[999] bg-black/15 dark:bg-black/40" (click)="closeNoteMenu()" aria-hidden="true"></div>
}
<div
  #noteMenuAnchorEl
  class="pointer-events-none fixed w-px"
  [style.left.px]="noteMenuAnchor.left"
  [style.top.px]="noteMenuAnchor.top"
  [style.height.px]="noteMenuAnchor.height"
  aria-hidden="true"></div>
<p-popover
  #noteMenuPopover
  appendTo="body"
  [focusOnShow]="false"
  (onShow)="onNoteMenuShown()"
  (onHide)="onNoteMenuHidden()"
  styleClass="note-action-popover w-[min(18rem,calc(100vw-1.5rem))] [&_.p-popover-content]:!p-0">
  <div class="flex max-h-[70dvh] flex-col overflow-hidden text-[var(--textColor)]">
    @if (noteMenuNote; as note) {
      <div class="flex items-start gap-2 border-b border-gray-200 py-2 pl-4 pr-1 dark:border-slate-700">
        <div class="min-w-0 flex-1 pt-1">
          <div class="truncate font-semibold">{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</div>
          <div class="truncate text-xs text-[var(--textColorFaded)]">{{ formatDate(note.last_modified) }} · {{ getSpaceName(note) }}</div>
        </div>
        <button
          type="button"
          class="inline-flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-full text-[var(--textColorFaded)] active:bg-gray-100 dark:active:bg-slate-800"
          (click)="closeNoteMenu()"
          aria-label="Close">
          <i class="pi pi-times" aria-hidden="true"></i>
        </button>
      </div>
    }
    <!-- Re-created whenever the menu swaps lists (main ↔ Export), so no focus/hover state carries over. -->
    @for (items of [noteMenuItems]; track items) {
    <p-menu
      [model]="items"
      styleClass="note-action-menu w-full overflow-y-auto !rounded-none !border-0 !bg-transparent !p-0 !py-1">
      <ng-template #item let-item>
        <a
          class="flex min-h-12 items-center gap-3 px-4 text-base"
          [ngClass]="item.state?.danger ? 'font-medium text-red-600 dark:text-red-400' : item.state?.muted ? 'text-[var(--textColorFaded)]' : 'text-[var(--textColor)]'">
          <i [class]="item.icon + ' !text-lg w-6 text-center'" aria-hidden="true"></i>
          <span class="flex-1">{{ item.label }}</span>
          @if (item.state?.submenu) {
            <i class="pi pi-chevron-right text-sm text-[var(--textColorFaded)]" aria-hidden="true"></i>
          }
        </a>
      </ng-template>
    </p-menu>
    }
  </div>
</p-popover>

<!-- Phone: "Edit tags" from the long-press menu — the same tag editor as the desktop Tags cell. -->
<p-dialog
  [visible]="!!tagDialogNote"
  (visibleChange)="!$event && closeTagEditor()"
  [modal]="true"
  [dismissableMask]="true"
  [draggable]="false"
  [resizable]="false"
  header="Tags"
  styleClass="w-[min(28rem,calc(100vw-1.5rem))]"
  appendTo="body">
  @if (tagDialogNote; as note) {
    <div class="mb-3 truncate text-sm font-semibold">{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</div>
    <app-note-tag-editor
      [note]="note"
      [catalog]="tags"
      [privateTagIds]="privateTagIdsFor(note)"
      [spaces]="spaces"
      [isOnline]="isOnline"
      (tagAdded)="noteTagChange.emit({ note, tag: $event, op: 'add' })"
      (tagRemoved)="noteTagChange.emit({ note, tag: $event, op: 'remove' })"
      (tagCreated)="tagCreated.emit($event)">
    </app-note-tag-editor>
  }
  <ng-template #footer>
    <p-button label="Done" (onClick)="closeTagEditor()"></p-button>
  </ng-template>
</p-dialog>

<app-bulk-action-dialog
  [visible]="noteDocxExportVisible"
  [action]="noteDocxExportAction"
  [selectedCount]="1"
  [showPageBreakBetweenNotesOption]="false"
  (cancel)="onNoteDocxExportCancel()"
  (submit)="onNoteDocxExportSubmit($event)">
</app-bulk-action-dialog>
}

<!-- Large Screen View -->
@if (isLargeScreen) {
<!-- No page navigation here: Home shows it in its fixed bottom area, right on top of the footer (see
     desktopPagerChange). -->
<div class="p-1">
  <p-table
    #desktopTable
    [value]="pageItems"
    [rowTrackBy]="trackByNoteId"
    [resizableColumns]="true"
    columnResizeMode="expand"
    stateStorage="local"
    [stateKey]="desktopTableStateKey"
    styleClass="notes-list-table p-datatable-sm p-datatable-gridlines text-[var(--textColor)] bg-primary border border-black rounded-md"
    tableStyleClass="w-full"
    (onColResize)="onColumnResizeEnd()"
  >
    <ng-template pTemplate="header">
      <tr>
        <!-- The first column holds only the Edit button (or, in select mode, the checkbox): no label, a fixed
             width just wide enough for it, and no resize handle. The width is inline !important on this header
             AND on the column's body cells, so it beats the restored (saved) column widths, applied to both. -->
        <th pResizableColumn class="list-fixed-col !text-center border border-black"
            [attr.style]="isSelectMode ? selectColumnStyle : editColumnStyle"
            [attr.aria-label]="isSelectMode ? 'Select' : 'Edit'"></th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Title')" (dblclick)="onHeaderDoubleClick('Title')">
          Title
          <span *ngIf="sortBy === 'Title'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Modified')" (dblclick)="onHeaderDoubleClick('Modified')">
          Modified
          <span *ngIf="sortBy === 'Modified'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Tags')" (dblclick)="onHeaderDoubleClick('Tags')">
          Tags
          <span *ngIf="sortBy === 'Tags'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Space')" (dblclick)="onHeaderDoubleClick('Space')">
          Space
          <span *ngIf="sortBy === 'Space'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Size')" (dblclick)="onHeaderDoubleClick('Size')">
          Size
          <span *ngIf="sortBy === 'Size'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Status')" (dblclick)="onHeaderDoubleClick('Status')">
          Status
          <span *ngIf="sortBy === 'Status'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer whitespace-nowrap" (click)="sortData('Priority')" (dblclick)="onHeaderDoubleClick('Priority')"
          title="Priority" aria-label="Priority">
          Prio
          <span *ngIf="sortBy === 'Priority'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer whitespace-nowrap" (click)="sortData('ActionRequired')" (dblclick)="onHeaderDoubleClick('ActionRequired')"
          title="Action required" aria-label="Action required">
          <i class="pi pi-flag-fill text-xs" aria-hidden="true"></i><span class="sr-only">Action required</span>
          <span *ngIf="sortBy === 'ActionRequired'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
        <th pResizableColumn class="!text-center border border-black cursor-pointer" (click)="sortData('Type')" (dblclick)="onHeaderDoubleClick('Type')">
          Type
          <span *ngIf="sortBy === 'Type'" class="material-icons text-xs align-middle">
            {{ sortOrder === 'asc' ? 'arrow_upward' : 'arrow_downward' }}
          </span>
        </th>
      </tr>
    </ng-template>
    <ng-template pTemplate="body" let-note>
      <tr
        class="hover:bg-primary-400 dark:hover:!bg-slate-700 dark:hover:!text-slate-100 cursor-pointer bg-primary-100 dark:bg-slate-800 text-black dark:text-slate-100"
        (click)="clickRow(note)"
        [ngClass]="{
          'bg-[var(--project4Color)]': note.ProjectName === 'Work',
          'bg-[var(--project1Color)]': note.ProjectName === 'NotesApp',
          'bg-[var(--project2Color)]': note.ProjectName === 'Vivi/Olivia',
          'bg-[var(--project3Color)]': note.ProjectName === 'Shopping',
          'font-bold': isEmphasizedStatus(note.Status),
          'italic text-[var(--textColorFaded)]': isMutedStatus(note.Status),
          'ring-2 ring-primary-600': isNoteSelected(note._id)
        }"
      >
        <td class="border border-[var(--borderColor)] !text-center" [attr.style]="isSelectMode ? selectColumnStyle : editColumnStyle">
          <!-- The icon at text height (no padding of its own) so the row keeps the table's 2px cell spacing. -->
          <div *ngIf="!isSelectMode; else selectModeDesktop" class="flex items-center justify-center">
            <button
              type="button"
              class="inline-flex items-center justify-center p-0 border-0 bg-transparent text-slate-700 hover:text-blue-600 cursor-pointer"
              (click)="navigateToEdit(note); $event.stopPropagation()"
              aria-label="Edit note"
              title="Edit note"
            >
              <span class="material-icons !text-base !leading-none">edit</span>
            </button>
          </div>
          <ng-template #selectModeDesktop>
            <p-checkbox
              [binary]="true"
              [ngModel]="isNoteSelected(note._id)"
              (ngModelChange)="toggleSelection(note, $event)"
              styleClass="cursor-pointer"
            ></p-checkbox>
          </ng-template>
        </td>
        <!-- Not yet synced: an animated red clock before the title (instead of a red title) until the sync succeeds. -->
        <td class="border border-[var(--borderColor)]"
          [ngClass]="{
            '!text-gray-500 dark:!text-gray-400': isMutedStatus(note.Status)
          }"
          [title]="isNotSynced(note) ? syncPendingLabel(note) : ''">@if (isNotSynced(note)) {<span class="sync-pending-clock" role="img" [attr.aria-label]="syncPendingLabel(note)"><svg viewBox="0 0 24 24" aria-hidden="true"><circle class="sync-pending-clock__face" cx="12" cy="12" r="9.25" /><line class="sync-pending-clock__hand sync-pending-clock__hand--hour" x1="12" y1="12" x2="12" y2="7.5" /><line class="sync-pending-clock__hand sync-pending-clock__hand--minute" x1="12" y1="12" x2="12" y2="5" /><circle cx="12" cy="12" r="1.3" class="sync-pending-clock__pin" /></svg></span>&nbsp;}<span data-testid="note-title" [class.line-through]="isStruckStatus(note.Status) || note.syncStatus === 'deleted'">{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</span><span *ngIf="isFavoriteNote(note)" title="Favorite"> ❤️</span><span *ngIf="note.Attachments?.length" title="Has attachments"> 📎</span></td>
        <td class="border border-[var(--borderColor)] !text-center">{{ formatDate(note.last_modified) }}</td>
        <!-- Tags: a click selects the cell (it never opens the note) and shows the tags with × and "Assign tag…",
             as in Edit; a click anywhere outside the cell unselects it. Not in Select mode (a click there selects the row). -->
        @if (tagEditNoteId === note._id && !isSelectMode) {
          <td
            class="note-tags-cell note-tags-cell--editing border border-[var(--borderColor)] max-w-0 !text-left"
            data-testid="tags-cell"
            (click)="$event.stopPropagation()"
            (keydown.escape)="closeTagEditor()">
            <app-note-tag-editor
              [note]="note"
              [catalog]="tags"
              [privateTagIds]="privateTagIdsFor(note)"
              [spaces]="spaces"
              [isOnline]="isOnline"
              [autoFocus]="true"
              (tagAdded)="noteTagChange.emit({ note, tag: $event, op: 'add' })"
              (tagRemoved)="noteTagChange.emit({ note, tag: $event, op: 'remove' })"
              (tagCreated)="tagCreated.emit($event)">
            </app-note-tag-editor>
          </td>
        } @else {
          <td
            class="note-tags-cell border border-[var(--borderColor)] max-w-0 !text-left"
            data-testid="tags-cell"
            [attr.title]="isSelectMode ? null : 'Click to add or remove tags'"
            (click)="onTagsCellClick($event, note)">
            <div class="flex flex-nowrap gap-1 overflow-x-auto">
              <span
                *ngFor="let tag of getTagDisplayItems(note)"
                class="note-tag-chip inline-flex flex-shrink-0 items-center gap-1 rounded-full border border-[var(--borderColor)] dark:border-slate-600 bg-white dark:bg-slate-800 px-2 py-0 text-xs leading-4 text-[var(--textColor)] dark:text-slate-100 whitespace-nowrap">
                <span
                  class="h-2.5 w-2.5 rounded-full border border-black/10 flex-shrink-0"
                  [style.backgroundColor]="tag.color || '#94a3b8'"
                  [title]="tag.color || 'No tag color'">
                </span>
                <i *ngIf="tag.isPrivate" class="pi pi-lock !text-[0.6rem] opacity-70" aria-label="Private tag"></i>
                <span>{{ tag.name }}</span>
              </span>
            </div>
          </td>
        }
        <td class="border border-[var(--borderColor)] whitespace-nowrap !text-center" data-testid="space-cell">
          <!-- Shared Space: red, with the "group" icon — same as the mobile card row. -->
          @if (isInSharedSpace(note)) {
            <span class="inline-flex items-center gap-0.5 align-top text-red-600 dark:text-red-400" title="Shared Space">
              <span class="material-symbols-outlined !text-sm leading-none" aria-hidden="true">group</span>{{ getSpaceName(note) }}
            </span>
          } @else {
            {{ getSpaceName(note) }}
          }
        </td>
        <td class="border border-[var(--borderColor)] whitespace-nowrap !text-center">{{ getNoteSize(note) }}</td>
        <td class="border border-[var(--borderColor)] !text-center">{{ note.Status }}</td>
        <td class="border border-[var(--borderColor)] !text-center" data-testid="priority-cell">
          @if (priorityIndicator(note.priority); as p) {
            @if (p.icon) {
              <span class="inline-flex not-italic" [ngClass]="p.colorClass" role="img" [attr.aria-label]="p.label" [title]="p.label" data-testid="priority-indicator"><i [ngClass]="p.icon" aria-hidden="true"></i></span>
            } @else {
              <span class="sr-only">{{ p.label }}</span>
            }
          }
        </td>
        <td class="border border-[var(--borderColor)] !text-center" data-testid="action-required-cell">
          @if (isActionRequired(note)) {
            <span class="inline-flex not-italic text-orange-600 dark:text-orange-400" role="img" aria-label="Action required" title="Action required" data-testid="action-required-indicator"><i class="pi pi-flag-fill" aria-hidden="true"></i></span>
          }
        </td>
        <td class="border border-[var(--borderColor)] !text-center">{{ note.NoteType }}</td>
      </tr>
    </ng-template>
  </p-table>
</div>
}

<!-- Print only (File → Print): a plain table of the notes on the current page, with the active filter
     after it. Moved into <body> (see ListComponent.printSheet); styles.scss hides everything else. -->
<div #printSheet class="notes-print-sheet" aria-hidden="true">
  <h1 class="notes-print-sheet__title">Notes{{ isFiltered ? ' (filtered)' : '' }}</h1>
  <p class="notes-print-sheet__meta">{{ printSummary }} · Printed {{ printedAt }}</p>
  <table class="notes-print-sheet__table">
    <!-- Title and Tags take the spare width; the other columns size to their words (never split mid-word). -->
    <colgroup>
      <col style="width: 34%" />
      <col />
      <col style="width: 20%" />
      <col span="6" />
    </colgroup>
    <thead>
      <tr>
        <th>Title</th>
        <th class="notes-print-sheet__center">Modified</th>
        <th>Tags</th>
        <th class="notes-print-sheet__center">Space</th>
        <th class="notes-print-sheet__center">Status</th>
        <th class="notes-print-sheet__center">Priority</th>
        <th class="notes-print-sheet__center" title="Action required">⚑</th>
        <th class="notes-print-sheet__center">Type</th>
        <th class="notes-print-sheet__center">Size</th>
      </tr>
    </thead>
    <tbody>
      @for (note of pageItems; track note._id) {
        <tr [class.notes-print-sheet__muted]="isMutedStatus(note.Status)" [class.notes-print-sheet__bold]="isEmphasizedStatus(note.Status)">
          <td><span [class.notes-print-sheet__struck]="isStruckStatus(note.Status) || note.syncStatus === 'deleted'">{{ note.Title | noteDisplayTitle: note.DescriptionHtml }}</span>@if (isFavoriteNote(note)) {<span> ❤️</span>}@if (note.Attachments?.length) {<span> 📎</span>}</td>
          <td class="notes-print-sheet__center">{{ formatDate(note.last_modified) }}</td>
          <td>{{ getTagNames(note) }}</td>
          <td class="notes-print-sheet__center notes-print-sheet__nowrap">
            @if (isInSharedSpace(note)) {
              <span class="notes-print-sheet__shared"><span class="material-symbols-outlined" aria-hidden="true">group</span>{{ getSpaceName(note) }}</span>
            } @else {
              {{ getSpaceName(note) }}
            }
          </td>
          <td class="notes-print-sheet__center">{{ note.Status }}</td>
          <td class="notes-print-sheet__center">{{ printPriority(note) }}</td>
          <td class="notes-print-sheet__center notes-print-sheet__flag">{{ isActionRequired(note) ? '⚑' : '' }}</td>
          <td class="notes-print-sheet__center">{{ note.NoteType }}</td>
          <td class="notes-print-sheet__center">{{ getNoteSize(note) }}</td>
        </tr>
      } @empty {
        <tr><td colspan="9">No notes to show.</td></tr>
      }
    </tbody>
  </table>
  @if (isFiltered) {
    <section class="notes-print-sheet__filter">
      <h2>Filter</h2>
      @if (printFilterSummary.length) {
        <dl>
          @for (row of printFilterSummary; track $index) {
            <dt>{{ row.label }}</dt>
            <dd>{{ row.value }}</dd>
          }
        </dl>
      } @else {
        <p>A filter is applied.</p>
      }
    </section>
  }
</div>
